import { useEffect, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { usePoseCapture } from 'contexts/PoseCaptureContext';



function CountdownManager({
  isModelLoading,
  checkingVisibility,
  isCountdownActive,
  poseCaptureComplete,
  isFullBodyVisible,
  isTooClose,
  distanceStatus,
  countdown,
  setCountdown,
  setIsCountdownActive,
  setIsTracking,
  setMissingPoints,
  setInitialLandmarks,
  webcamRef,
  poseLandmarkerRef,
  postureName,
  initialCountdown,
  setInitialCountdown,
  missingPoints,
  setFinalLandmarks,
  startCountdown
}) {
  const timerRef = useRef(null);
  const hasCapturedRef = useRef(false);
  const [stopReason, setStopReason] = useState(null);
  const visibilityTimeoutRef = useRef(null);
  const { capturePoseFn } = usePoseCapture();

  const stopCountdown = useCallback(() => {
    console.log('stopCountdown called with state:', {
      initialCountdown,
      countdown,
      isCountdownActive
    });
    setCountdown(null);
    setInitialCountdown(null);
    setIsCountdownActive(false);
    setIsTracking(false);
    setMissingPoints([]);
    setInitialLandmarks(null);
    if (timerRef.current) {
      console.log('Clearing countdown timer');
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (visibilityTimeoutRef.current) {
      console.log('Clearing visibility timeout');
      clearTimeout(visibilityTimeoutRef.current);
      visibilityTimeoutRef.current = null;
    }
  }, [setCountdown, setInitialCountdown, setIsCountdownActive, setIsTracking, setMissingPoints, setInitialLandmarks]);

  // Expose startCountdown to parent (VoiceSynthesizer)
  useEffect(() => {
    startCountdown.current = () => {
      console.log('startCountdown called with conditions:', {
        isCountdownActive,
        initialCountdown,
        countdown,
        hasCaptured: hasCapturedRef.current,
        poseCaptureComplete,
        isFullBodyVisible,
        isTooClose,
        distanceStatus
      });
      if (
        !isCountdownActive &&
        initialCountdown === null &&
        countdown === null &&
        !hasCapturedRef.current &&
        !poseCaptureComplete &&
        isFullBodyVisible &&
        !isTooClose &&
        distanceStatus === 'good distance'
      ) {
        console.log('Starting initial 7-second countdown');
        setInitialCountdown(7);
        setIsCountdownActive(true);
      } else {
        console.error('Countdown conditions not met:', {
          isCountdownActive,
          initialCountdown,
          countdown,
          hasCaptured: hasCapturedRef.current,
          poseCaptureComplete,
          isFullBodyVisible,
          isTooClose,
          distanceStatus
        });
      }
    };
  }, [
    startCountdown,
    isCountdownActive,
    initialCountdown,
    countdown,
    poseCaptureComplete,
    isFullBodyVisible,
    isTooClose,
    distanceStatus,
    setInitialCountdown,
    setIsCountdownActive
  ]);

  useEffect(() => {
    if (!webcamRef?.current || !poseLandmarkerRef?.current || isModelLoading || !checkingVisibility || poseCaptureComplete) {
      console.log('Stopping countdown due to invalid conditions:', {
        webcamReady: !!webcamRef?.current,
        poseLandmarkerReady: !!poseLandmarkerRef?.current,
        isModelLoading,
        checkingVisibility,
        poseCaptureComplete
      });
      stopCountdown();
      return;
    }

    if (!isFullBodyVisible || isTooClose || distanceStatus !== 'good distance') {
      if (visibilityTimeoutRef.current) {
        console.log('Visibility timeout already set, waiting...');
        return;
      }
      console.log('Setting visibility timeout due to:', {
        isFullBodyVisible,
        isTooClose,
        distanceStatus
      });
      visibilityTimeoutRef.current = setTimeout(() => {
        console.log('Visibility timeout triggered, stopping countdown');
        setStopReason(!isFullBodyVisible ? 'missing_body' : isTooClose ? 'too_close' : 'too_far');
      }, 2000); // Increased to 2 seconds to avoid premature stops
      return;
    }

    if (visibilityTimeoutRef.current) {
      console.log('Clearing visibility timeout');
      clearTimeout(visibilityTimeoutRef.current);
      visibilityTimeoutRef.current = null;
    }

    if (initialCountdown !== null || countdown !== null) {
      console.log('Starting countdown timer with:', { initialCountdown, countdown });
      timerRef.current = setInterval(() => {
        if (!webcamRef.current || !poseLandmarkerRef.current) {
          console.log('Stopping countdown: refs missing');
          setStopReason('refs_missing');
          return;
        }

        if (!isFullBodyVisible || isTooClose || distanceStatus !== 'good distance') {
          if (visibilityTimeoutRef.current) {
            console.log('Visibility timeout already set in timer, waiting...');
            return;
          }
          console.log('Setting visibility timeout in timer due to:', {
            isFullBodyVisible,
            isTooClose,
            distanceStatus
          });
          visibilityTimeoutRef.current = setTimeout(() => {
            console.log('Visibility timeout triggered in timer, stopping countdown');
            setStopReason(!isFullBodyVisible ? 'missing_body' : isTooClose ? 'too_close' : 'too_far');
          }, 2000);
          return;
        }

        if (visibilityTimeoutRef.current) {
          console.log('Clearing visibility timeout in timer');
          clearTimeout(visibilityTimeoutRef.current);
          visibilityTimeoutRef.current = null;
        }

        if (initialCountdown !== null) {
          console.log('Updating initialCountdown:', initialCountdown);
          setInitialCountdown((prev) => {
            if (prev <= 1) {
              console.log('Initial countdown reached 1, performing pose detection');
              const video = webcamRef.current.video;
              if (video && video.videoWidth && video.videoHeight && poseLandmarkerRef.current?.detectForVideo) {
                try {
                  const detection = poseLandmarkerRef.current.detectForVideo(video, performance.now());
                  const handleResults = (results) => {
                    if (results?.landmarks?.[0]) {
                      console.log('Pose detected, setting landmarks and starting main countdown');
                      setInitialLandmarks(results.landmarks[0]);
                      setFinalLandmarks(results.landmarks[0]);
                      setCountdown(3);
                      setIsTracking(true);
                      setMissingPoints([]);
                      setInitialCountdown(null);
                    } else {
                      console.log('No pose detected');
                      setStopReason('no_pose');
                    }
                  };
                  if (detection && typeof detection.then === 'function') {
                    detection.then(handleResults).catch((err) => {
                      console.error('Pose detection failed:', err);
                      toast.error(`Pose detection failed: ${err.message}`);
                      setStopReason('detection_failed');
                    });
                  } else {
                    handleResults(detection);
                  }
                } catch (err) {
                  console.error('Pose detection error:', err);
                  toast.error(`Pose detection error: ${err.message}`);
                  setStopReason('detection_failed');
                }
              } else {
                console.log('Webcam or pose detection not ready');
                setStopReason('not_ready');
              }
              return null;
            }
            console.log('Decrementing initialCountdown to:', prev - 1);
            return prev - 1;
          });
        } else if (countdown !== null) {
          console.log('Updating countdown:', countdown);
          setCountdown((prev) => {
            if (prev <= 1) {
              if (capturePoseFn && !hasCapturedRef.current) {
                console.log('Capturing pose');
                hasCapturedRef.current = true;
                capturePoseFn();
              } else if (hasCapturedRef.current) {
                console.log('Pose capture failed');
                setStopReason('capture_failed');
                return null;
              }
              // Without a registered capture function the recording is driven
              // by PoseCaptureHandler once the full body is visible, so the
              // countdown simply ends here rather than reporting a failure.
              setIsCountdownActive(false);
              setIsTracking(false);
              setCountdown(null);
              stopCountdown();
              return null;
            }
            console.log('Decrementing countdown to:', prev - 1);
            return prev - 1;
          });
        }
      }, 1000);
    }

    return () => {
      console.log('Cleaning up countdown timer');
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
        visibilityTimeoutRef.current = null;
      }
    };
  }, [
    isModelLoading,
    checkingVisibility,
    poseCaptureComplete,
    isFullBodyVisible,
    isTooClose,
    distanceStatus,
    initialCountdown,
    countdown,
    isCountdownActive,
    webcamRef,
    poseLandmarkerRef,
    capturePoseFn,
    setInitialCountdown,
    setCountdown,
    setIsCountdownActive,
    setIsTracking,
    setMissingPoints,
    setInitialLandmarks,
    setFinalLandmarks,
    stopCountdown
  ]);

  useEffect(() => {
    if (stopReason) {
      console.log('Stopping countdown due to:', stopReason);
      stopCountdown();
      if (stopReason === 'missing_body') {
        toast.error(`Please ensure your full body is visible. Missing: ${missingPoints.length ? missingPoints.join(', ') : 'entire body'}`);
      } else if (stopReason === 'too_close') {
        toast.error('You are too close to the camera. Please move back.');
      } else if (stopReason === 'too_far') {
        toast.error('You are too far from the camera. Please move closer.');
      } else if (stopReason === 'no_pose') {
        toast.error('No pose detected. Please ensure full body is visible.');
      } else if (stopReason === 'detection_failed') {
        toast.error('Pose detection failed. Please try again.');
      } else if (stopReason === 'not_ready') {
        toast.error('Webcam or pose detection model not ready.');
      } else if (stopReason === 'refs_missing') {
        toast.error('System not ready. Please ensure webcam and pose detection are active.');
      } else if (stopReason === 'capture_failed') {
        toast.error('Failed to capture pose. Please try again.');
      }
      setStopReason(null);
    }
  }, [stopReason, missingPoints, stopCountdown]);

  useEffect(() => {
    if (countdown === null && initialCountdown === null) {
      console.log('Resetting hasCapturedRef due to countdown completion');
      hasCapturedRef.current = false;
    }
  }, [countdown, initialCountdown]);

  return null;
}

export default CountdownManager;
